import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';

function InviteAccept() {
  const [isLoading, setIsLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  const { inviteCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loginAsGuest } = useAuth();

  useEffect(() => {
    loadInvitation();
  }, [inviteCode]);

  const loadInvitation = async () => {
    try {
      const invite = await authService.getInvitation(inviteCode);
      setInvitation(invite);
    } catch (error) {
      console.error('Failed to load invitation:', error);
      // Create mock invitation for development
      setInvitation({
        id: 'mock-invite',
        code: inviteCode,
        type: 'general',
        role: 'viewer',
        description: 'Join CloudStream Demo',
        streamId: searchParams.get('stream'),
        requireApproval: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      
      if (!isAuthenticated) {
        // For guests, create temporary account
        await loginAsGuest(`Guest_${Math.random().toString(36).substr(2, 6)}`);
      }

      await authService.acceptInvitation(inviteCode);
      
      // Redirect based on invitation type
      if (invitation.streamId) {
        navigate(`/viewer/${invitation.streamId}`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      setError('Failed to accept invitation');
      setIsLoading(false);
    }
  };

  const handleDecline = () => {
    navigate('/login');
  };

  if (isLoading && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cf-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="card">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Invalid Invitation</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="card text-center">
          <div className="w-16 h-16 bg-cf-orange rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-2xl font-bold">CS</span>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-4">You're Invited!</h1>
          
          {invitation && (
            <div className="space-y-6">
              <div className="bg-cf-orange bg-opacity-10 border border-cf-orange rounded-lg p-4">
                <h2 className="text-lg font-semibold text-cf-orange mb-2">
                  {invitation.description || 'Join CloudStream'}
                </h2>
                <p className="text-gray-400 text-sm">
                  Invitation Code: <span className="font-mono font-bold">{inviteCode}</span>
                </p>
              </div>
              
              <div className="bg-gray-700 rounded-lg p-4 text-left space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Type:</span>
                  <span className="text-white capitalize">{invitation.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Role:</span>
                  <span className="text-white capitalize">{invitation.role}</span>
                </div>
                {invitation.streamId && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Stream:</span>
                    <span className="text-white font-mono text-sm">{invitation.streamId}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Approval:</span>
                  <span className="text-white">
                    {invitation.requireApproval ? 'Required' : 'Not Required'}
                  </span>
                </div>
              </div>
              
              {error && (
                <div className="bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  onClick={handleAccept}
                  disabled={isLoading}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Accepting...</span>
                    </>
                  ) : (
                    <span>Accept Invitation</span>
                  )}
                </button>
                
                <button
                  onClick={handleDecline}
                  disabled={isLoading}
                  className="flex-1 btn-secondary"
                >
                  Decline
                </button>
              </div>
              
              <p className="text-gray-500 text-xs">
                By accepting this invitation, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InviteAccept; 